import { compact, first, isEmpty, sortBy, uniqueId } from 'lodash';

import { EstateWaterfallComparisonWaterfall } from '@/modules/estateWaterfall/components/EstateWaterfallComparison.type';
import {
  DEFAULT_NEXT_STEP_LABEL,
  DEFAULT_OPPORTUNITY_LABEL,
  DEFAULT_RATIONALE_LABEL,
} from '@/modules/presentation/slides/ObservationsAndOpportunitiesSlide/ObservationsAndOpportunitiesSlide.components';
import {
  AugmentedCreateComparedWaterfallInput,
  AugmentedCreatePresentationBundleInput,
  PresentationBundleKind,
  PresentationPageKind,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { UnreachableError } from '@/utils/errors';
import { getNodes } from '@/utils/graphqlUtils';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import { NEW_ROW_ID_SENTINEL } from './ClientPresentationDesigner.constants';
import { ClientPresentationShape } from './ClientPresentationDesigner.types';
import {
  BUNDLE_TO_DISPLAY_NAME,
  isSinglePageBundle,
} from './components/ClientPresentationDesignerTreeView/ClientPresentationDesignerTreeView.constants';
import { EMPTY_ESTATE_INSIGHTS_BUNDLE_CONFIGURATION } from './components/settingsModals/EstateInsightsBundleSettingsModal/EstateInsightsBundleSettingsModal.utils';
import {
  EDUCATIONAL_PAGE_KINDS,
  EducationalPageKind,
  getEducationalPageDefaultDisplayNames,
} from './components/slides/EducationalSlide/EducationalSlide.utils';
import {
  ClientPresentationDesignerV2_ComparedWaterfall_EstateWaterfallFragment,
  ClientPresentationDesignerV2_ComparedWaterfallFragment,
  ClientPresentationDesignerV2_EstateWaterfallFragment,
  ClientPresentationDesignerV2_PresentationBundleFragment,
  ClientPresentationDesignerV2_PresentationPageFragment,
  ClientPresentationDesignerV2_WaterfallBundleConfigurationFragment,
  ClientPresentationV2_ClientPresentationV2Fragment,
} from './graphql/ClientPresentationDesignerV2.generated';
import { mapPageToCreateInput } from './save/mapPageToCreateInput';
import { ClientPresentationV2Bundle } from './types/ClientPresentation.PresentationBundleType';
import { ClientPresentationV2Page } from './types/ClientPresentation.PresentationPageType';
import { mapCitationsToCreateInput } from './utils/presentationItemCitations';

/**
 * Computes the number of empty spacer columns to pad a multi-column
 * slide row. On the final page, columns expand to fill width — except
 * a single item gets one spacer for half-width. Earlier pages pad
 * fully to keep column widths consistent across slides.
 */
export function getEmptySpacerCount({
  visibleItemCount,
  maxColumns,
  hasMorePages,
}: {
  visibleItemCount: number;
  maxColumns: number;
  hasMorePages: boolean;
}): number {
  if (visibleItemCount >= maxColumns) return 0;
  if (hasMorePages) return maxColumns - visibleItemCount;
  if (visibleItemCount === 1) return 1;
  return 0;
}

export function isLegacySinglePageDiagramBundle(
  bundle: ClientPresentationV2Bundle
): boolean {
  if (
    bundle.type !== PresentationBundleKind.CustomPageBundle &&
    bundle.type !== PresentationBundleKind.DiagramBundle
  ) {
    return false;
  }
  return first(bundle.pages)?.type === PresentationPageKind.DiagramPage;
}

export const NEW_BUNDLE_ID_SENTINEL = 'NEW_BUNDLE' as const;
export const NEW_PAGE_ID_SENTINEL = 'NEW_PAGE' as const;
export { NEW_ROW_ID_SENTINEL } from './ClientPresentationDesigner.constants';
export const SLIDE_ID_QUERY_PARAM = 'slideId';

export function getNewBundleId(type: PresentationBundleKind): string {
  return `${NEW_BUNDLE_ID_SENTINEL}-${type}-${uniqueId()}`;
}

export function getNewPageId(type: PresentationPageKind): string {
  return `${NEW_PAGE_ID_SENTINEL}-${type}-${uniqueId()}`;
}

export function getNewRowId(): string {
  return `${NEW_ROW_ID_SENTINEL}-${uniqueId()}`;
}

export function isNewBundle(bundle: ClientPresentationV2Bundle): boolean {
  return bundle.id.startsWith(NEW_BUNDLE_ID_SENTINEL);
}

export function isNewPage(page: ClientPresentationV2Page): boolean {
  return page.id.startsWith(NEW_PAGE_ID_SENTINEL);
}

/**
 * Resolve a stale bundle ID to its counterpart in a new bundles array after
 * sync has replaced sentinel IDs with real server IDs. Both arrays must be
 * in the same order (sync preserves ordering).
 */
export function remapBundle(
  bundleId: string,
  oldBundles: ClientPresentationV2Bundle[],
  newBundles: ClientPresentationV2Bundle[]
): ClientPresentationV2Bundle | undefined {
  const index = oldBundles.findIndex((b) => b.id === bundleId);
  return index >= 0 ? newBundles[index] : undefined;
}

/**
 * Resolve a stale page ID to its counterpart in a new bundles array after
 * sync. Searches all bundles for the page, then returns the page at the
 * same position in the new array.
 */
export function remapPage(
  pageId: string,
  oldBundles: ClientPresentationV2Bundle[],
  newBundles: ClientPresentationV2Bundle[]
): ClientPresentationV2Page | undefined {
  for (let i = 0; i < oldBundles.length; i++) {
    const pageIndex = oldBundles[i]!.pages.findIndex((p) => p.id === pageId);
    if (pageIndex >= 0) {
      return newBundles[i]?.pages[pageIndex];
    }
  }
  return undefined;
}

/** Id to select to land on a bundle: the bundle id, or its first page. */
export function getBundleSelectionId(
  bundle: ClientPresentationV2Bundle
): string {
  if (isSinglePageBundle(bundle.type)) {
    return bundle.id;
  }
  return first(bundle.pages)?.id || bundle.id;
}

export function mapClientPresentationV2ToForm(
  presentation: ClientPresentationV2_ClientPresentationV2Fragment,
  householdId: string,
  // When set, selects the bundle at this index instead of using the URL.
  // Used after AI generation to reselect by position (ids may have changed).
  preferredBundleIndex?: number,
  preferredPageIndex?: number
): ClientPresentationShape {
  let selectedItemId = '';
  const slideId = new URLSearchParams(window.location.search).get(
    SLIDE_ID_QUERY_PARAM
  );

  const sortedPresentationBundles = sortBy(
    getNodes(presentation.bundles),
    'index'
  );
  const bundles: ClientPresentationV2Bundle[] = compact(
    sortedPresentationBundles.map<ClientPresentationV2Bundle | null>((bundle) =>
      mapBundleToForm(bundle, householdId, presentation.id)
    )
  );

  const allBundleIds = bundles.reduce<string[]>((acc, bundle) => {
    acc.push(bundle.id);
    if (isSinglePageBundle(bundle.type)) {
      bundle.pages.forEach((page) => acc.push(page.id));
    }

    return acc;
  }, []);

  const preferredBundle =
    preferredBundleIndex !== undefined
      ? bundles[preferredBundleIndex]
      : undefined;
  const firstBundle = first(bundles);
  if (preferredBundle) {
    const preferredPage =
      preferredPageIndex !== undefined
        ? preferredBundle.pages[preferredPageIndex]
        : undefined;
    selectedItemId = preferredPage?.id ?? getBundleSelectionId(preferredBundle);
  } else if (slideId && allBundleIds.includes(slideId)) {
    selectedItemId = slideId;
  } else if (firstBundle) {
    selectedItemId = getBundleSelectionId(firstBundle);
  }

  return {
    id: presentation.id,
    title: presentation.name,
    bundles,
    presentationConfiguration: {
      showPageNumbers: presentation.showPageNumbers,
      showPageNumbersOnCoverSlide: presentation.showPageNumbersOnCoverSlide,
      pageNumberToStartFrom: presentation.pageNumberToStartFrom,
      showLegalDisclaimer: presentation.showLegalDisclaimer,
      showClientNamesInHeader: presentation.showClientNamesInHeader ?? true,
    },
    selectedItemId,
  };
}

export function getBundleDisplayName(
  bundle: ClientPresentationDesignerV2_PresentationBundleFragment
): string {
  const { kind } = bundle;
  if (kind === PresentationBundleKind.EducationPageBundle) {
    const page = first(getNodes(bundle.pages));

    // this is just a type assertion to make the type checker happy; there should never be a non-education page
    // in an education bundle. (we log an error for this below)
    if (
      page?.kind &&
      EDUCATIONAL_PAGE_KINDS.includes(page.kind as EducationalPageKind)
    ) {
      const displayName =
        getEducationalPageDefaultDisplayNames()[
          page.kind as EducationalPageKind
        ];
      if (displayName) {
        return displayName;
      }
    } else {
      diagnostics.error(
        `Unknown educational page kind: ${page?.kind} for bundle ${bundle.id}`
      );
    }
  } else if (kind === PresentationBundleKind.DocumentSummaryBundle) {
    const page = first(getNodes(bundle.pages));
    return (
      page?.documentSummaryPage?.summary?.documentv2?.name ?? 'Document summary'
    );
  } else if (kind === PresentationBundleKind.EntityGraphViewBundle) {
    return (
      bundle.entityGraphViewBundleConfiguration?.displayName ??
      BUNDLE_TO_DISPLAY_NAME[kind]
    );
  } else if (kind === PresentationBundleKind.EntitiesOverviewBundle) {
    return (
      bundle.entitiesOverviewBundleConfiguration?.displayName ||
      BUNDLE_TO_DISPLAY_NAME[kind]
    );
  } else if (kind === PresentationBundleKind.AssetDistributionBundle) {
    // Title slot in the sidebar / TOC takes the user-edited
    // `displayName` directly (falling back to the "Asset distribution"
    // default); the `deathOrderDisplay` lives in the subtitle slot so
    // multiple asset-distribution bundles in the same deck can be
    // told apart by their death-order context.
    return (
      bundle.assetDistributionBundleConfiguration?.displayName ||
      BUNDLE_TO_DISPLAY_NAME[kind]
    );
  }

  return BUNDLE_TO_DISPLAY_NAME[kind];
}

export function mapBundleToForm(
  bundle: ClientPresentationDesignerV2_PresentationBundleFragment,
  householdId: string,
  presentationId: string
): ClientPresentationV2Bundle | null {
  const {
    coverPageConfiguration,
    waterfallBundleConfiguration,
    entitySummaryBundleConfiguration,
    customPageBundleConfiguration,
    waterfallComparisonBundleConfiguration,
    estateInsightsBundleConfiguration,
    entityGraphViewBundleConfiguration,
    beneficiariesBundleConfiguration,
    entitiesOverviewBundleConfiguration,
    dividerBundleConfiguration,
    balanceSheetBundleConfiguration,
    documentsReviewedBundleConfiguration,
    trustOverviewBundleConfiguration,
    businessOverviewBundleConfiguration,
    familyOverviewBundleConfiguration,
    willBundleConfiguration,
    powerOfAttorneyBundleConfiguration,
    healthcareDirectiveBundleConfiguration,
    assetDistributionBundleConfiguration,
    planningOpportunitiesBundleConfiguration,
    kind: type,
    id,
    templateNotes,
    excludeFromOutput,
  } = bundle;
  const pages = compact(getNodes(bundle.pages).map(mapPageToForm));

  const displayName = getBundleDisplayName(bundle);

  const output: ClientPresentationV2Bundle = {
    id,
    type,
    displayName,
    pages,
    templateNotes: templateNotes ?? undefined,
    excludeFromOutput: excludeFromOutput || undefined,
  };

  switch (type) {
    case PresentationBundleKind.CoverSlideBundle: {
      if (!coverPageConfiguration) {
        break;
      }
      const { customText } = coverPageConfiguration;
      output.coverSlideBundleConfiguration = {
        id: coverPageConfiguration.id,
        customText: customText ?? '',
      };
      break;
    }
    case PresentationBundleKind.WaterfallOverviewBundle: {
      if (!waterfallBundleConfiguration) {
        diagnostics.warn(
          `No waterfall bundle configuration found for [${householdId}, ${presentationId}, ${id}]`
        );
        break;
      }

      const {
        includeAssumptionsPage,
        includeDiagramPage,
        includeSummaryPage,
        includeSummaryBars,
        includeSummaryTiles,
        showSummaryTileDetails,
        useUpdatedSummaryPage,
        includeOverview,
        includeBeforeFirstDeath,
        includeAfterFirstDeath,
        includeAfterSecondDeath,
        summaryHideZeroDollarItems,
        includeTaxOverviewPage,
        includeCoverPage,
        firstDeathYear,
        secondDeathYear,
        growthProfile,
        estateWaterfall,
        displayName: configDisplayName,
      } = waterfallBundleConfiguration;

      if (configDisplayName) {
        output.displayName = configDisplayName;
      }

      output.waterfallOverviewBundleConfiguration = {
        id: waterfallBundleConfiguration.id,
        includeCoverPage,
        includeAssumptionsPage,
        includeDiagramPage,
        includeSummaryPage,
        includeSummaryBars: includeSummaryBars ?? true,
        includeSummaryTiles: includeSummaryTiles ?? true,
        showSummaryTileDetails: showSummaryTileDetails ?? true,
        // A bundle saved before these fields existed reports them as null.
        // The updated page is opt-in, so it defaults off; its sections
        // default on, matching the other include flags.
        useUpdatedSummaryPage: useUpdatedSummaryPage ?? false,
        includeOverview: includeOverview ?? true,
        includeBeforeFirstDeath: includeBeforeFirstDeath ?? true,
        includeAfterFirstDeath: includeAfterFirstDeath ?? true,
        includeAfterSecondDeath: includeAfterSecondDeath ?? true,
        summaryHideZeroDollarItems: summaryHideZeroDollarItems ?? false,
        includeTaxOverviewPage,
        firstDeathYear: firstDeathYear ?? new Date().getFullYear(),
        secondDeathYear,
        growthProfileId: growthProfile?.id,
        estateWaterfallId: estateWaterfall?.id,
      };
      break;
    }
    case PresentationBundleKind.EntitySummaryBundle: {
      if (!entitySummaryBundleConfiguration) {
        diagnostics.warn(
          `No entity summary bundle configuration found for [${householdId}, ${presentationId}, ${id}]`
        );
        break;
      }

      const {
        entity: { id: entityId },
        deathOrder: { id: deathOrderId },
        includeEntitySummaryPage,
        includeBeneficiariesAndDispositionsPage,
        includeEntityNotesOnSummaryPage,
        includeDiagramAndNotesPage,
        includeBalanceSheetPage,
        includeBalanceSheetExportPage,
        includeInsurancePolicySlides,
      } = entitySummaryBundleConfiguration;

      output.entitySummaryConfiguration = {
        id: entitySummaryBundleConfiguration.id,
        entityId,
        includeEntitySummaryPage: includeEntitySummaryPage ?? false,
        includeDiagramAndNotesPage: includeDiagramAndNotesPage ?? false,
        includeBalanceSheetPage: includeBalanceSheetPage ?? false,
        includeBalanceSheetExportPage: includeBalanceSheetExportPage ?? false,
        includeBeneficiariesAndDispositionsPage:
          includeBeneficiariesAndDispositionsPage ?? false,
        deathOrderId,
        deathOrderNotes:
          entitySummaryBundleConfiguration.entity.deathOrderNotes?.find(
            (d) => d.clientProfile?.id === deathOrderId
          )?.notes || '',
        includeEntityNotesOnSummaryPage:
          includeEntityNotesOnSummaryPage ?? false,
        includeInsurancePolicySlides: includeInsurancePolicySlides ?? false,
      };
      if (entitySummaryBundleConfiguration.entity.subtype.displayName) {
        output.displayName =
          entitySummaryBundleConfiguration.entity.subtype.displayName;
      }
      break;
    }
    case PresentationBundleKind.CustomPageBundle: {
      const page = first(pages);
      if (page?.type === PresentationPageKind.CustomPage) {
        output.displayName = page.customPage?.pageName ?? output.displayName;
      } else if (page?.type === PresentationPageKind.DiagramPage) {
        output.displayName = page.diagramPage?.pageName ?? output.displayName;
      }
      if (!customPageBundleConfiguration) {
        output.customPageBundleConfiguration = {
          includePageHeader: false,
          pageHeader: '',
          pageSubheader: '',
        };
      } else {
        output.customPageBundleConfiguration = {
          id: customPageBundleConfiguration.id,
          includePageHeader:
            customPageBundleConfiguration.includePageHeader ?? false,
          pageHeader: customPageBundleConfiguration.pageHeader ?? '',
          pageSubheader: customPageBundleConfiguration.pageSubheader ?? '',
        };
      }

      break;
    }
    case PresentationBundleKind.WaterfallComparisonBundle: {
      if (!waterfallComparisonBundleConfiguration) {
        // Preserve the bundle with empty config so it appears in the editor
        // with a missing-config warning, rather than being silently dropped.
        output.waterfallComparisonBundleConfiguration = {
          waterfalls: [],
          includeAssumptionsPage: true,
          includeCoverPage: true,
        };
        break;
      }
      const {
        includeAssumptionsPage,
        includeCoverPage,
        comparedWaterfalls,
        displayName,
      } = waterfallComparisonBundleConfiguration;
      if (displayName) {
        output.displayName = displayName;
      }
      output.waterfallComparisonBundleConfiguration = {
        id: waterfallComparisonBundleConfiguration.id,
        waterfalls:
          comparedWaterfalls?.map<EstateWaterfallComparisonWaterfall>(
            (cwf) => ({
              // The row's own id, so a save can update it rather than replace it.
              id: cwf.id,
              displayName: cwf.displayName,
              waterfallId: cwf.estateWaterfall?.id || '',
              firstDeathYear: cwf.firstDeathYear || new Date().getFullYear(),
              secondDeathYear: cwf.secondDeathYear || undefined,
              growthProfileId: cwf.growthProfile?.id || '',
            })
          ) || [],
        includeAssumptionsPage,
        includeCoverPage,
      };
      break;
    }
    case PresentationBundleKind.EstateInsightsBundle: {
      if (!estateInsightsBundleConfiguration) {
        output.estateInsightsBundleConfiguration = {
          ...EMPTY_ESTATE_INSIGHTS_BUNDLE_CONFIGURATION,
          estateInsights: [],
        };
      } else {
        output.estateInsightsBundleConfiguration = {
          id: estateInsightsBundleConfiguration.id,
          includeExSpouseListedInWillPage:
            estateInsightsBundleConfiguration.includeExSpouseListedInWillPage,
          includeMissingGuardianProvisionsPage:
            estateInsightsBundleConfiguration.includeMissingGuardianProvisionsPage,
          includeNoAlternativeAgentListedPoaPage:
            estateInsightsBundleConfiguration.includeNoAlternativeAgentListedPoaPage,
          includeNoAlternativeAgentListedHealthcareProxyPage:
            estateInsightsBundleConfiguration.includeNoAlternativeAgentListedHealthcareProxyPage,
          includeTrusteesNotUpdatedPage:
            estateInsightsBundleConfiguration.includeTrusteesNotUpdatedPage,
          includeExSpouseListedAsTrusteePage:
            estateInsightsBundleConfiguration.includeExSpouseListedAsTrusteePage,
          includeExSpouseListedAsBeneficiaryPage:
            estateInsightsBundleConfiguration.includeExSpouseListedAsBeneficiaryPage,
          includeAutomaticOutrightDistributionToSpousePage:
            estateInsightsBundleConfiguration.includeAutomaticOutrightDistributionToSpousePage,
          includeMissingPoaForSurivivingSpousePage:
            estateInsightsBundleConfiguration.includeMissingPoaForSurivivingSpousePage,
          includeOutrightDistributionsToChildrenPage:
            estateInsightsBundleConfiguration.includeOutrightDistributionsToChildrenPage,
          includeBypassTrustFundedSolelyViaDisclaimerPage:
            estateInsightsBundleConfiguration.includeBypassTrustFundedSolelyViaDisclaimerPage,
          includeCorporateTrusteePage:
            estateInsightsBundleConfiguration.includeCorporateTrusteePage,
          includeAnnualExclusionGiftingPage:
            estateInsightsBundleConfiguration.includeAnnualExclusionGiftingPage,
          includeFullUseOfLifetimeExemptionPage:
            estateInsightsBundleConfiguration.includeFullUseOfLifetimeExemptionPage,
          includeOpen529AccountPage:
            estateInsightsBundleConfiguration.includeOpen529AccountPage,
          includeMissingDocumentsPage:
            estateInsightsBundleConfiguration.includeMissingDocumentsPage,
          includeTrusteeDomiciledInDifferentState:
            estateInsightsBundleConfiguration.includeTrusteeDomiciledInDifferentState,
          includeRetitleAssetsToRevocableTrustPage:
            estateInsightsBundleConfiguration.includeRetitleAssetsToRevocableTrustPage,
          includeGiftToCharityPage:
            estateInsightsBundleConfiguration.includeGiftToCharityPage,
          includeRetirementAccountBeneficiaryDesignationsPage:
            estateInsightsBundleConfiguration.includeRetirementAccountBeneficiaryDesignationsPage,
          includeSpecificBequestsPage:
            estateInsightsBundleConfiguration.includeSpecificBequestsPage,
          includeDifferentStateOfResidenceFromEstateDocumentsPage:
            estateInsightsBundleConfiguration.includeDifferentStateOfResidenceFromEstateDocumentsPage,
          estateInsights:
            estateInsightsBundleConfiguration.estateInsights ?? [],
        };
      }
      break;
    }
    case PresentationBundleKind.EntityGraphViewBundle: {
      if (!entityGraphViewBundleConfiguration) {
        diagnostics.warn(
          `No entity graph view bundle configuration found for bundle ${id}`
        );
        break;
      }

      output.entityGraphViewBundleConfiguration = {
        id: entityGraphViewBundleConfiguration.id,
        displayName: entityGraphViewBundleConfiguration.displayName ?? '',
        entityGraphViewId:
          entityGraphViewBundleConfiguration.entityGraphView?.id ?? null,
      };
      break;
    }
    case PresentationBundleKind.BeneficiaryReportBundle: {
      if (!beneficiariesBundleConfiguration) {
        diagnostics.warn(
          `No beneficiaries bundle configuration found for bundle ${id}`
        );
        break;
      }

      let individualOverviewIDs: string[] = [];
      individualOverviewIDs = individualOverviewIDs.concat(
        beneficiariesBundleConfiguration.individualOverviewProfiles?.map(
          (cp) => cp.id
        ) ?? []
      );
      individualOverviewIDs = individualOverviewIDs.concat(
        beneficiariesBundleConfiguration.individualOverviewOrganizations?.map(
          (co) => co.id
        ) ?? []
      );

      let householdOverviewIDs: string[] = [];
      householdOverviewIDs = householdOverviewIDs.concat(
        beneficiariesBundleConfiguration.householdOverviewProfiles?.map(
          (hp) => hp.id
        ) ?? []
      );
      householdOverviewIDs = householdOverviewIDs.concat(
        beneficiariesBundleConfiguration.householdOverviewOrganizations?.map(
          (ho) => ho.id
        ) ?? []
      );

      output.beneficiariesBundleConfiguration = {
        id: beneficiariesBundleConfiguration.id,
        displayName: beneficiariesBundleConfiguration.displayName ?? '',
        includeHouseholdOverview:
          beneficiariesBundleConfiguration.includeHouseholdOverview ?? false,
        includeIndividualOverviews:
          beneficiariesBundleConfiguration.includeIndividualOverviews ?? false,
        includeDetailedBreakdownsInIndividualOverviews:
          beneficiariesBundleConfiguration.includeDetailedBreakdownsInIndividualOverviews ??
          false,
        firstDeathYear:
          beneficiariesBundleConfiguration.firstDeathYear ??
          new Date().getFullYear(),
        secondDeathYear:
          beneficiariesBundleConfiguration.secondDeathYear ?? undefined,
        growthProfileId:
          beneficiariesBundleConfiguration.growthProfile?.id ?? null,
        estateWaterfallId:
          beneficiariesBundleConfiguration.estateWaterfall?.id ?? null,
        individualOverviewIDs,
        householdOverviewIDs,
      };
      break;
    }
    case PresentationBundleKind.EntitiesOverviewBundle: {
      // we want to support the following cases:
      // 1. the feature flag is off, and we don't have a configuration, or don't care about respecting it.
      // 2. the feature flag is on, and we don't have a configuration; that's okay! we'll return the default;.
      // 3. the feature flag is on, and we have a configuration; we'll return the configuration.

      // this is fine! we'll render the default in the slide, and when the user saves,
      // we'll save the default.
      if (!entitiesOverviewBundleConfiguration) {
        break;
      }

      const configuredEntities = getNodes(
        entitiesOverviewBundleConfiguration.entities
      );
      output.entitiesOverviewConfiguration = {
        id: entitiesOverviewBundleConfiguration.id,
        displayName: entitiesOverviewBundleConfiguration.displayName ?? null,
        showSummaryMetrics:
          entitiesOverviewBundleConfiguration.showSummaryMetrics,
        // if an empty list, we'll default to showing everythig downstream
        entities:
          compact(
            configuredEntities.map((entity) => {
              if (!entity.entity) {
                return null;
              }
              return {
                id: entity.id,
                index: entity.index,
                isHidden: entity.isHidden,
                entity: entity.entity,
              };
            })
          ) ?? [],
      };
      break;
    }
    case PresentationBundleKind.DividerBundle: {
      output.dividerBundleConfiguration = {
        id: dividerBundleConfiguration?.id,
        dividerTitle: dividerBundleConfiguration?.dividerTitle ?? '',
        customContent: dividerBundleConfiguration?.customContent ?? '',
      };
      break;
    }
    case PresentationBundleKind.BalanceSheetBundle: {
      if (balanceSheetBundleConfiguration) {
        // Strip __typename (from GraphQL) and keep all config fields
        const { __typename: _, ...config } = balanceSheetBundleConfiguration;
        output.balanceSheetBundleConfiguration = config;
        output.pages = output.pages.map((page) => {
          if (page.balanceSheetBundleConfiguration?.id !== config.id) {
            return page;
          }

          // Legacy pages inherit the bundle configuration through the page
          // resolver. Keep it only on the bundle so the form has the same shape
          // before and after the resolver's lazy configuration seeding.
          const {
            balanceSheetBundleConfiguration: _inheritedConfiguration,
            ...pageWithoutInheritedConfiguration
          } = page;
          return pageWithoutInheritedConfiguration;
        });
      }
      break;
    }
    case PresentationBundleKind.DocumentsReviewedBundle: {
      if (!documentsReviewedBundleConfiguration) break;

      // Each `node` already carries `sortOrder`; resolve it client-side
      // so a server returning out-of-order edges still renders correctly.
      // The bundle-level `displayName` is the user-editable slide title
      // (kept null when unset so the slide renders the default).
      const reviewedDocuments = getNodes(
        documentsReviewedBundleConfiguration.reviewedDocuments
      );
      const documents = [...reviewedDocuments]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(({ id: documentId, documentName, dateDisplay }) => ({
          id: documentId,
          documentName,
          dateDisplay,
        }));
      output.documentsReviewedBundleConfiguration = {
        id: documentsReviewedBundleConfiguration.id,
        displayName: documentsReviewedBundleConfiguration.displayName ?? null,
        documents,
      };
      break;
    }
    case PresentationBundleKind.TrustOverviewBundle: {
      if (!trustOverviewBundleConfiguration) break;

      // Two nested connections: top-level trusts, and each trust's
      // items. Sort both client-side by `sortOrder` so a server
      // returning out-of-order edges still renders correctly. Each
      // trust's `displayName` maps to the form's `label`; the
      // bundle-level `displayName` is the user-editable slide title
      // (kept null when unset so the slide renders the default).
      const trusts = [...getNodes(trustOverviewBundleConfiguration.trusts)]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(({ id: trustId, displayName, trustItems }) => ({
          id: trustId,
          label: displayName,
          items: [...getNodes(trustItems)]
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(({ id: itemId, label, body, citations }) => ({
              id: itemId,
              label,
              body,
              citations: citations ?? [],
            })),
        }));
      output.trustOverviewBundleConfiguration = {
        id: trustOverviewBundleConfiguration.id,
        displayName: trustOverviewBundleConfiguration.displayName ?? null,
        itemsPerPage: trustOverviewBundleConfiguration.itemsPerPage,
        trusts,
      };
      break;
    }
    case PresentationBundleKind.FamilyOverviewBundle: {
      if (!familyOverviewBundleConfiguration) break;

      // Each `node` already carries `sortOrder`; resolve it client-side
      // so a server returning out-of-order edges still renders correctly.
      // The bundle-level `displayName` is the user-editable slide title
      // (kept null when unset so the slide renders the default).
      const memberNodes = getNodes(familyOverviewBundleConfiguration.members);
      const members = [...memberNodes]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(
          ({ id: memberId, name, relationship, description, citations }) => ({
            id: memberId,
            name,
            relationship,
            description,
            citations: citations ?? [],
          })
        );
      output.familyOverviewBundleConfiguration = {
        id: familyOverviewBundleConfiguration.id,
        displayName: familyOverviewBundleConfiguration.displayName ?? null,
        members,
      };
      break;
    }
    case PresentationBundleKind.WillBundle: {
      if (!willBundleConfiguration) break;

      // Two nested connections: top-level clients (one per grantor) and
      // each client's items. Sort both client-side by `sortOrder` so a
      // server returning out-of-order edges still renders correctly.
      // The server's `client` field (a free-form string seeded from the
      // grantor record) maps to the form's `grantorName`; the
      // bundle-level `displayName` is the user-editable slide title
      // (kept null when unset so the slide renders the default).
      const wills = [...getNodes(willBundleConfiguration.clients)]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(({ id: willId, client, willItems }) => ({
          id: willId,
          grantorName: client,
          items: [...getNodes(willItems)]
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(({ id: itemId, label, body, citations }) => ({
              id: itemId,
              label,
              body,
              citations: citations ?? [],
            })),
        }));
      output.willBundleConfiguration = {
        id: willBundleConfiguration.id,
        displayName: willBundleConfiguration.displayName ?? null,
        itemsPerPage: willBundleConfiguration.itemsPerPage,
        wills,
      };
      break;
    }
    case PresentationBundleKind.PowerOfAttorneyBundle: {
      if (!powerOfAttorneyBundleConfiguration) break;

      // Mirrors the WillBundle case — same two nested connections
      // (clients, each client's items), same client-side sort, same
      // displayName-as-slide-title convention. The schema differs only
      // in the nested-list name (`powerOfAttorneyItems` vs `willItems`).
      const powersOfAttorney = [
        ...getNodes(powerOfAttorneyBundleConfiguration.clients),
      ]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(({ id: poaId, client, powerOfAttorneyItems }) => ({
          id: poaId,
          grantorName: client,
          items: [...getNodes(powerOfAttorneyItems)]
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(({ id: itemId, label, body, citations }) => ({
              id: itemId,
              label,
              body,
              citations: citations ?? [],
            })),
        }));
      output.powerOfAttorneyBundleConfiguration = {
        id: powerOfAttorneyBundleConfiguration.id,
        displayName: powerOfAttorneyBundleConfiguration.displayName ?? null,
        itemsPerPage: powerOfAttorneyBundleConfiguration.itemsPerPage,
        powersOfAttorney,
      };
      break;
    }
    case PresentationBundleKind.HealthcareDirectiveBundle: {
      if (!healthcareDirectiveBundleConfiguration) break;

      // Mirrors the WillBundle case — same two nested connections
      // (clients, each client's items), same client-side sort, same
      // displayName-as-slide-title convention. The schema differs only
      // in the nested-list name (`healthcareDirectiveItems` vs
      // `willItems`).
      const healthcareDirectives = [
        ...getNodes(healthcareDirectiveBundleConfiguration.clients),
      ]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(({ id: hcdId, client, healthcareDirectiveItems }) => ({
          id: hcdId,
          grantorName: client,
          items: [...getNodes(healthcareDirectiveItems)]
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(({ id: itemId, label, body, citations }) => ({
              id: itemId,
              label,
              body,
              citations: citations ?? [],
            })),
        }));
      output.healthcareDirectiveBundleConfiguration = {
        id: healthcareDirectiveBundleConfiguration.id,
        displayName: healthcareDirectiveBundleConfiguration.displayName ?? null,
        itemsPerPage: healthcareDirectiveBundleConfiguration.itemsPerPage,
        healthcareDirectives,
      };
      break;
    }
    case PresentationBundleKind.AssetDistributionBundle: {
      if (!assetDistributionBundleConfiguration) break;

      // Two nested connections: top-level distribution items, and each
      // item's detail rows. Sort both client-side by `sortOrder` so a
      // server returning out-of-order edges still renders correctly.
      // The bundle-level `displayName` is the user-editable slide
      // title (kept null when unset so the slide renders the default);
      // `deathOrderDisplay` and `description` are required strings on
      // the schema, defaulted to empty when not yet set so the slide's
      // empty-state placeholders kick in.
      const items = [
        ...getNodes(assetDistributionBundleConfiguration.distributionItems),
      ]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(({ id: itemId, displayName, itemDetails }) => ({
          id: itemId,
          displayName,
          itemDetails: [...getNodes(itemDetails)]
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(({ id: detailId, label, body, citations }) => ({
              id: detailId,
              label,
              body,
              citations: citations ?? [],
            })),
        }));
      output.assetDistributionBundleConfiguration = {
        id: assetDistributionBundleConfiguration.id,
        displayName: assetDistributionBundleConfiguration.displayName ?? null,
        itemsPerPage: assetDistributionBundleConfiguration.itemsPerPage,
        deathOrderDisplay:
          assetDistributionBundleConfiguration.deathOrderDisplay ?? '',
        description: assetDistributionBundleConfiguration.description ?? '',
        items,
      };
      break;
    }
    case PresentationBundleKind.PlanningOpportunitiesBundle: {
      if (!planningOpportunitiesBundleConfiguration) break;

      // Items map 1:1 with cards on the slide — each backend item
      // holds all six fields (opportunity/rationale/next-step ×
      // label/body) for a single card. The new fields are optional on
      // the schema (legacy `label`/`body` are deprecated and ignored
      // here). Empty/null section labels are defaulted to the
      // canonical placeholders so freshly-seeded items already read
      // as "OPPORTUNITY / RATIONALE / NEXT STEP" in the UI and the
      // next save persists those defaults to the backend; bodies
      // default to empty strings since they have no canonical default.
      // Sort client-side by `sortOrder` so a server returning
      // out-of-order edges still renders in the right order.
      const opportunities = [
        ...getNodes(planningOpportunitiesBundleConfiguration.items),
      ]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((item) => ({
          id: item.id,
          opportunityLabel: item.opportunityLabel || DEFAULT_OPPORTUNITY_LABEL,
          opportunityBody: item.opportunityBody ?? '',
          rationaleLabel: item.rationaleLabel || DEFAULT_RATIONALE_LABEL,
          rationaleBody: item.rationaleBody ?? '',
          nextStepLabel: item.nextStepLabel || DEFAULT_NEXT_STEP_LABEL,
          nextStepBody: item.nextStepBody ?? '',
        }));
      output.planningOpportunitiesBundleConfiguration = {
        id: planningOpportunitiesBundleConfiguration.id,
        displayName:
          planningOpportunitiesBundleConfiguration.displayName ?? null,
        opportunities,
      };
      break;
    }
    case PresentationBundleKind.BusinessOverviewBundle: {
      if (!businessOverviewBundleConfiguration) break;

      // Same two-level shape as the trusts overview: sort businesses
      // and each business's items by `sortOrder`. Each business's
      // `displayName` maps to the form's `label`.
      const businesses = [
        ...getNodes(businessOverviewBundleConfiguration.businesses),
      ]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(({ id: businessId, displayName, businessItems }) => ({
          id: businessId,
          label: displayName,
          items: [...getNodes(businessItems)]
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(({ id: itemId, label, body, citations }) => ({
              id: itemId,
              label,
              body,
              citations: citations ?? [],
            })),
        }));
      output.businessOverviewBundleConfiguration = {
        id: businessOverviewBundleConfiguration.id,
        displayName: businessOverviewBundleConfiguration.displayName ?? null,
        itemsPerPage: businessOverviewBundleConfiguration.itemsPerPage,
        businesses,
      };
      break;
    }
    // no configuration object for these bundles
    case PresentationBundleKind.DiagramBundle:
    case PresentationBundleKind.DocumentSummaryBundle:
    case PresentationBundleKind.EducationPageBundle:
    case PresentationBundleKind.TableOfContentsBundle:
    case PresentationBundleKind.LuminaryDisclaimerBundle:
    case PresentationBundleKind.ProfessionalTeamBundle:
    case PresentationBundleKind.StandaloneDisclaimerBundle:
    case PresentationBundleKind.FamilyTreeBundle:
    case PresentationBundleKind.LiabilitiesBundle:
    case PresentationBundleKind.LifeInsuranceBundle:
      break;
    default: {
      throw new UnreachableError({
        case: type,
        message: 'Unhandled bundle type',
      });
    }
  }

  return output;
}

export function mapPageToForm(
  page: ClientPresentationDesignerV2_PresentationPageFragment
): ClientPresentationV2Page | null {
  const output: ClientPresentationV2Page = {
    id: page.id,
    type: page.kind,
    customPage: page.customPage ?? undefined,
    diagramPage: page.diagramPage ?? undefined,
    presentationBundleCoverPage: page.presentationBundleCoverPage ?? undefined,
    documentSummaryPage: page.documentSummaryPage ?? undefined,
  };

  switch (page.kind) {
    case PresentationPageKind.CoverPage: {
      output.coverPage = {
        id: page.coverPage?.id,
        customContent: page.coverPage?.customContent ?? '',
        displayTitle: page.coverPage?.displayTitle ?? '',
        customPrompt: page.coverPage?.customPrompt ?? null,
      };
      break;
    }
    case PresentationPageKind.PresentationBundleCoverPage:
    case PresentationPageKind.WaterfallComparisonCoverPage: {
      output.presentationBundleCoverPage = {
        id: page.presentationBundleCoverPage?.id,
        content: page.presentationBundleCoverPage?.content ?? '',
      };
      break;
    }
    // A waterfall cover page is a different server row than a cover page, so its
    // id comes from `waterfallBundleCoverPage` even though the form reuses the
    // `coverPage` slot to hold it.
    case PresentationPageKind.WaterfallCoverPage: {
      output.coverPage = {
        id: page.waterfallBundleCoverPage?.id,
        customContent: page.waterfallBundleCoverPage?.content ?? '',
        displayTitle: '',
      };
      break;
    }
    case PresentationPageKind.DocumentSummaryPage: {
      if (!page.documentSummaryPage) return null;

      output.documentSummaryPage = {
        id: page.documentSummaryPage.id,
        documentType: page.documentSummaryPage.documentType,
        relevantClientProfiles: page.documentSummaryPage.relevantClientProfiles,
        summary: page.documentSummaryPage.summary,
      };
      break;
    }
    case PresentationPageKind.BeneficiaryReportBeneficiary: {
      // Template-created pages may not have a beneficiaryID yet; keep them
      // as placeholders so they appear in the sidebar tree view.
      if (page.beneficiaryPage?.beneficiaryID) {
        output.beneficiaryPage = {
          id: page.beneficiaryPage.id,
          beneficiaryID: page.beneficiaryPage.beneficiaryID,
        };
      }
      break;
    }
    case PresentationPageKind.DiagramPage: {
      // AI-generated diagrams initially persist the parent page without its
      // diagram row. Keep that page in form state so save reconciliation can
      // adopt its server ID and the slide can show its missing-content state if
      // generation fails.
      if (page.diagramPage) {
        output.diagramPage = {
          id: page.diagramPage.id,
          pageName: page.diagramPage.pageName,
          customPrompt: page.diagramPage.customPrompt,
          nodeConfigurations: page.diagramPage.nodeConfigurations,
          edgeDefinitions: page.diagramPage.edgeDefinitions,
          phases: page.diagramPage.phases,
        };
      }
      break;
    }
    case PresentationPageKind.BalanceSheetPage:
    case PresentationPageKind.BalanceSheetExportPage: {
      if (page.balanceSheetBundleConfiguration) {
        const { __typename: _, ...config } =
          page.balanceSheetBundleConfiguration;
        output.balanceSheetBundleConfiguration = config;
      }
      break;
    }
  }

  return output;
}

/**
 * Returns true when a bundle or its required configuration object is absent.
 * Used both to show the sidebar alert icon and to block auto-save.
 */
export function isBundleMissingConfig(
  bundle: ClientPresentationV2Bundle | null | undefined
): boolean {
  if (!bundle) return true;

  switch (bundle.type) {
    case PresentationBundleKind.WaterfallOverviewBundle:
      return !bundle.waterfallOverviewBundleConfiguration;
    case PresentationBundleKind.EntitySummaryBundle:
      return !bundle.entitySummaryConfiguration;
    case PresentationBundleKind.WaterfallComparisonBundle:
      return (
        !bundle.waterfallComparisonBundleConfiguration ||
        isEmpty(bundle.waterfallComparisonBundleConfiguration.waterfalls)
      );
    case PresentationBundleKind.EntityGraphViewBundle:
      return !bundle.entityGraphViewBundleConfiguration;
    case PresentationBundleKind.BeneficiaryReportBundle:
      return !bundle.beneficiariesBundleConfiguration;
    case PresentationBundleKind.AssetDistributionBundle:
      return !bundle.assetDistributionBundleConfiguration;
    case PresentationBundleKind.DocumentsReviewedBundle:
      return !bundle.documentsReviewedBundleConfiguration;
    case PresentationBundleKind.FamilyOverviewBundle:
      return !bundle.familyOverviewBundleConfiguration;
    case PresentationBundleKind.HealthcareDirectiveBundle:
      return !bundle.healthcareDirectiveBundleConfiguration;
    case PresentationBundleKind.PlanningOpportunitiesBundle:
      return !bundle.planningOpportunitiesBundleConfiguration;
    case PresentationBundleKind.PowerOfAttorneyBundle:
      return !bundle.powerOfAttorneyBundleConfiguration;
    case PresentationBundleKind.TrustOverviewBundle:
      return !bundle.trustOverviewBundleConfiguration;
    case PresentationBundleKind.BusinessOverviewBundle:
      return !bundle.businessOverviewBundleConfiguration;
    case PresentationBundleKind.WillBundle:
      return !bundle.willBundleConfiguration;
    default:
      return false;
  }
}

export function mapBundleToUpdateInput(
  bundle: ClientPresentationV2Bundle,
  householdID: string,
  /**
   * Explicit position in the presentation. Omit (or pass `undefined`) when
   * appending a bundle whose final position should be decided by the
   * backend's natural creation-order — the next autosave will stamp a real
   * index for it.
   */
  index?: number
): AugmentedCreatePresentationBundleInput {
  const { type: kind } = bundle;
  const output: AugmentedCreatePresentationBundleInput = {
    create: {
      kind,
      householdID,
      index,
      // Neither field is editable in the designer, but every persist clears and
      // recreates all bundles, so echo them back or a template-created
      // presentation loses them on its first autosave.
      templateNotes: bundle.templateNotes,
      excludeFromOutput: bundle.excludeFromOutput,
    },
    // For AI-generated bundles (added via the picker's AI-generated
    // section), this tells the backend to spawn a
    // PresentationTemplatePromptGeneration workflow that fills in the
    // bundle's content.
    generate: bundle.pendingGenerate ? true : undefined,
    withPages: bundle.pages.map((page, index) =>
      mapPageToCreateInput(page, householdID, index)
    ),
  };

  switch (kind) {
    case PresentationBundleKind.CoverSlideBundle: {
      if (!bundle.coverSlideBundleConfiguration) {
        break;
      }
      output.create.coverPageConfigurationID;
      // TODO - i think BE needs to add the cover slide bundle configuration to the input type
      break;
    }
    case PresentationBundleKind.WaterfallOverviewBundle: {
      if (!bundle.waterfallOverviewBundleConfiguration) {
        break;
      }

      const {
        includeCoverPage,
        includeAssumptionsPage,
        includeDiagramPage,
        includeSummaryPage,
        includeSummaryBars,
        includeSummaryTiles,
        showSummaryTileDetails,
        useUpdatedSummaryPage,
        includeOverview,
        includeBeforeFirstDeath,
        includeAfterFirstDeath,
        includeAfterSecondDeath,
        summaryHideZeroDollarItems,
        includeTaxOverviewPage,
        firstDeathYear,
        secondDeathYear,
        growthProfileId,
        estateWaterfallId,
      } = bundle.waterfallOverviewBundleConfiguration;

      output.withWaterfallBundleConfiguration = {
        create: {
          displayName: bundle.displayName,
          firstDeathYear,
          secondDeathYear,
          growthProfileID: growthProfileId,
          estateWaterfallID: estateWaterfallId,
          includeCoverPage,
          includeAssumptionsPage,
          includeDiagramPage,
          includeSummaryPage,
          includeSummaryBars,
          includeSummaryTiles,
          showSummaryTileDetails,
          useUpdatedSummaryPage,
          includeOverview,
          includeBeforeFirstDeath,
          includeAfterFirstDeath,
          includeAfterSecondDeath,
          summaryHideZeroDollarItems,
          includeTaxOverviewPage,
          householdID,
        },
      };
      break;
    }
    case PresentationBundleKind.EntitySummaryBundle: {
      if (!bundle.entitySummaryConfiguration) {
        break;
      }
      const {
        entityId,
        includeEntitySummaryPage,
        includeBeneficiariesAndDispositionsPage,
        includeBalanceSheetPage,
        includeBalanceSheetExportPage,
        deathOrderId: deathOrderID,
        includeEntityNotesOnSummaryPage,
      } = bundle.entitySummaryConfiguration;
      output.withEntitySummaryBundleConfiguration = {
        create: {
          entityID: entityId,
          householdID,
          deathOrderID,
          includeBeneficiariesAndDispositionsPage,
          includeEntitySummaryPage,
          includeBalanceSheetPage,
          includeBalanceSheetExportPage,
          includeEntityNotesOnSummaryPage,
        },
      };
      break;
    }
    case PresentationBundleKind.CustomPageBundle: {
      if (!bundle.customPageBundleConfiguration) {
        throw new Error(
          `No custom page bundle configuration found for bundle ${bundle.id}`
        );
      }
      const { includePageHeader, pageHeader, pageSubheader } =
        bundle.customPageBundleConfiguration;
      output.withCustomPageBundleConfiguration = {
        create: {
          householdID,
          includePageHeader,
          pageHeader,
          pageSubheader,
        },
      };
      break;
    }
    case PresentationBundleKind.WaterfallComparisonBundle: {
      if (!bundle.waterfallComparisonBundleConfiguration) {
        break;
      }
      const {
        includeAssumptionsPage,
        includeCoverPage,
        waterfalls: comparedWaterfalls,
      } = bundle.waterfallComparisonBundleConfiguration;

      output.withWaterfallComparisonBundleConfiguration = {
        create: {
          householdID,
          displayName: bundle.displayName,
          includeAssumptionsPage,
          includeCoverPage,
        },
        withComparedWaterfalls:
          comparedWaterfalls.map<AugmentedCreateComparedWaterfallInput>(
            (cwf) => ({
              create: {
                householdID,
                estateWaterfallID: cwf.waterfallId,
                displayName: cwf.displayName,
                firstDeathYear: cwf.firstDeathYear,
                secondDeathYear: cwf.secondDeathYear,
                growthProfileID: cwf.growthProfileId,
              },
            })
          ),
      };
      break;
    }
    case PresentationBundleKind.EstateInsightsBundle: {
      if (!bundle.estateInsightsBundleConfiguration) {
        throw new Error(
          `No estate insights bundle configuration found for bundle ${bundle.id}`
        );
      }
      output.withEstateInsightsBundleConfiguration = {
        create: {
          householdID,
          includeExSpouseListedInWillPage:
            bundle.estateInsightsBundleConfiguration
              .includeExSpouseListedInWillPage,
          includeMissingGuardianProvisionsPage:
            bundle.estateInsightsBundleConfiguration
              .includeMissingGuardianProvisionsPage,
          includeNoAlternativeAgentListedPoaPage:
            bundle.estateInsightsBundleConfiguration
              .includeNoAlternativeAgentListedPoaPage,
          includeNoAlternativeAgentListedHealthcareProxyPage:
            bundle.estateInsightsBundleConfiguration
              .includeNoAlternativeAgentListedHealthcareProxyPage,
          includeTrusteesNotUpdatedPage:
            bundle.estateInsightsBundleConfiguration
              .includeTrusteesNotUpdatedPage,
          includeExSpouseListedAsTrusteePage:
            bundle.estateInsightsBundleConfiguration
              .includeExSpouseListedAsTrusteePage,
          includeExSpouseListedAsBeneficiaryPage:
            bundle.estateInsightsBundleConfiguration
              .includeExSpouseListedAsBeneficiaryPage,
          includeAutomaticOutrightDistributionToSpousePage:
            bundle.estateInsightsBundleConfiguration
              .includeAutomaticOutrightDistributionToSpousePage,
          includeMissingPoaForSurivivingSpousePage:
            bundle.estateInsightsBundleConfiguration
              .includeMissingPoaForSurivivingSpousePage,
          includeOutrightDistributionsToChildrenPage:
            bundle.estateInsightsBundleConfiguration
              .includeOutrightDistributionsToChildrenPage,
          includeBypassTrustFundedSolelyViaDisclaimerPage:
            bundle.estateInsightsBundleConfiguration
              .includeBypassTrustFundedSolelyViaDisclaimerPage,
          includeCorporateTrusteePage:
            bundle.estateInsightsBundleConfiguration
              .includeCorporateTrusteePage,
          includeAnnualExclusionGiftingPage:
            bundle.estateInsightsBundleConfiguration
              .includeAnnualExclusionGiftingPage,
          includeFullUseOfLifetimeExemptionPage:
            bundle.estateInsightsBundleConfiguration
              .includeFullUseOfLifetimeExemptionPage,
          includeOpen529AccountPage:
            bundle.estateInsightsBundleConfiguration.includeOpen529AccountPage,
          includeMissingDocumentsPage:
            bundle.estateInsightsBundleConfiguration
              .includeMissingDocumentsPage,
          includeSpecificBequestsPage:
            bundle.estateInsightsBundleConfiguration
              .includeSpecificBequestsPage,
          includeTrusteeDomiciledInDifferentState:
            bundle.estateInsightsBundleConfiguration
              .includeTrusteeDomiciledInDifferentState,
          includeDifferentStateOfResidenceFromEstateDocumentsPage:
            bundle.estateInsightsBundleConfiguration
              .includeDifferentStateOfResidenceFromEstateDocumentsPage,
          includeRetitleAssetsToRevocableTrustPage:
            bundle.estateInsightsBundleConfiguration
              .includeRetitleAssetsToRevocableTrustPage,
          includeGiftToCharityPage:
            bundle.estateInsightsBundleConfiguration.includeGiftToCharityPage,
          includeRetirementAccountBeneficiaryDesignationsPage:
            bundle.estateInsightsBundleConfiguration
              .includeRetirementAccountBeneficiaryDesignationsPage,
          estateInsightIDs: (
            bundle?.estateInsightsBundleConfiguration?.estateInsights ?? []
          ).map((ei) => ei.id),
        },
      };
      break;
    }
    case PresentationBundleKind.EntityGraphViewBundle: {
      if (!bundle.entityGraphViewBundleConfiguration) {
        break;
      }

      const { entityGraphViewId, displayName } =
        bundle.entityGraphViewBundleConfiguration;

      if (!entityGraphViewId) {
        diagnostics.warn(
          `Skipping entity graph view bundle ${bundle.id} because it has no entity graph view ID`
        );
        break;
      }

      output.withEntityGraphViewBundleConfiguration = {
        create: {
          householdID,
          entityGraphViewID: entityGraphViewId,
          displayName,
        },
      };
      break;
    }
    case PresentationBundleKind.EntitiesOverviewBundle: {
      // Configuration is optional - when the feature flag is off, bundles won't have it.
      // When the flag is on, the drawer ensures configuration exists.
      if (bundle.entitiesOverviewConfiguration) {
        const { showSummaryMetrics, entities } =
          bundle.entitiesOverviewConfiguration;
        output.withEntitiesOverviewBundleConfiguration = {
          create: {
            householdID,
            displayName: bundle.displayName,
            showSummaryMetrics,
          },
          withEntities: compact(
            entities.map((entity, index) => {
              if (!entity.entity) {
                return null;
              }
              return {
                create: {
                  householdID,
                  entityID: entity.entity.id,
                  index: entity.index ?? index,
                  isHidden: entity.isHidden ?? false,
                },
              };
            })
          ),
        };
      }
      break;
    }
    case PresentationBundleKind.BeneficiaryReportBundle: {
      if (!bundle.beneficiariesBundleConfiguration) {
        diagnostics.warn(
          `Skipping beneficiary report bundle ${bundle.id} because it has no estate waterfall ID`
        );
        break;
      }

      const {
        displayName,
        includeHouseholdOverview,
        includeIndividualOverviews,
        includeDetailedBreakdownsInIndividualOverviews,
        firstDeathYear,
        secondDeathYear,
        growthProfileId,
        estateWaterfallId,
        individualOverviewIDs,
        householdOverviewIDs,
      } = bundle.beneficiariesBundleConfiguration;

      output.withBeneficiariesBundleConfiguration = {
        create: {
          householdID,
          displayName,
          includeHouseholdOverview,
          includeIndividualOverviews,
          includeDetailedBreakdownsInIndividualOverviews,
          firstDeathYear,
          secondDeathYear,
          growthProfileID: growthProfileId,
          estateWaterfallID: estateWaterfallId ?? undefined,
          householdOverviewProfileIDs: householdOverviewIDs.filter(
            (id) => getPulidKind(id) === PulidKind.ClientProfile
          ),
          householdOverviewOrganizationIDs: householdOverviewIDs.filter(
            (id) => getPulidKind(id) === PulidKind.ClientOrganization
          ),
          individualOverviewProfileIDs: individualOverviewIDs.filter(
            (id) => getPulidKind(id) === PulidKind.ClientProfile
          ),
          individualOverviewOrganizationIDs: individualOverviewIDs.filter(
            (id) => getPulidKind(id) === PulidKind.ClientOrganization
          ),
        },
      };
      break;
    }
    case PresentationBundleKind.DividerBundle: {
      output.withDividerBundleConfiguration = {
        create: {
          householdID,
          dividerTitle: bundle.dividerBundleConfiguration?.dividerTitle ?? '',
          customContent: bundle.dividerBundleConfiguration?.customContent ?? '',
        },
      };
      break;
    }
    case PresentationBundleKind.BalanceSheetBundle: {
      if (bundle.balanceSheetBundleConfiguration) {
        const {
          id: _configId,
          hiddenColumns,
          ...configFields
        } = bundle.balanceSheetBundleConfiguration;
        output.withBalanceSheetBundleConfiguration = {
          create: {
            householdID,
            ...configFields,
            // columnOrder is write-only (not returned by the API) — the
            // backend uses it to determine the order of columns in the
            // balance sheet response. Pass an empty array on create so the
            // backend uses its default ordering.
            columnOrder: [],
            hiddenColumns: hiddenColumns ?? [],
          },
        };
      }
      break;
    }
    case PresentationBundleKind.DocumentsReviewedBundle: {
      const documents =
        bundle.documentsReviewedBundleConfiguration?.documents ?? [];
      if (!bundle.documentsReviewedBundleConfiguration) break;
      const documentsReviewedDisplayName =
        bundle.documentsReviewedBundleConfiguration?.displayName ?? undefined;
      output.withDocumentsReviewedBundleConfiguration = {
        create: {
          householdID,
          displayName: documentsReviewedDisplayName,
        },
        // Nested children: each save recreates the bundle config (the
        // server mints fresh PresentationDocumentsReviewedDocument IDs).
        // `syncBundlesFromResponse` keeps the form's existing IDs stable
        // across persists, so React keys / hover tracking survive saves.
        withReviewedDocuments: documents.map((doc, sortOrder) => ({
          create: {
            householdID,
            documentName: doc.documentName,
            dateDisplay: doc.dateDisplay,
            sortOrder,
          },
        })),
      };
      break;
    }
    case PresentationBundleKind.TrustOverviewBundle: {
      const trusts = bundle.trustOverviewBundleConfiguration?.trusts ?? [];
      if (!bundle.trustOverviewBundleConfiguration) break;
      const trustOverviewDisplayName =
        bundle.trustOverviewBundleConfiguration?.displayName ?? undefined;
      output.withTrustOverviewBundleConfiguration = {
        create: {
          householdID,
          displayName: trustOverviewDisplayName,
          itemsPerPage: bundle.trustOverviewBundleConfiguration?.itemsPerPage,
        },
        // Two-level nested children: each save recreates the bundle
        // config plus all trust configurations and their items. The
        // server mints fresh ids; `syncBundlesFromResponse` keeps the
        // form's existing ids stable across persists, so React keys /
        // hover tracking survive saves.
        withTrusts: trusts.map((trust, trustSortOrder) => ({
          create: {
            householdID,
            displayName: trust.label,
            sortOrder: trustSortOrder,
          },
          withTrustItems: trust.items.map((item, itemSortOrder) => ({
            create: {
              householdID,
              label: item.label,
              body: item.body,
              sortOrder: itemSortOrder,
            },
            // Re-sent because the recreate cascade-deletes the backend's
            // generated citations along with the old items.
            withCitations: mapCitationsToCreateInput(
              item.citations,
              householdID
            ),
          })),
        })),
      };
      break;
    }
    case PresentationBundleKind.FamilyOverviewBundle: {
      const members = bundle.familyOverviewBundleConfiguration?.members ?? [];
      if (!bundle.familyOverviewBundleConfiguration) break;
      const familyOverviewDisplayName =
        bundle.familyOverviewBundleConfiguration?.displayName ?? undefined;
      output.withFamilyOverviewBundleConfiguration = {
        create: {
          householdID,
          displayName: familyOverviewDisplayName,
        },
        // Nested children: each save recreates the bundle config (the
        // server mints fresh PresentationFamilyOverviewMemberConfiguration
        // IDs). `syncBundlesFromResponse` keeps the form's existing IDs
        // stable across persists, so React keys / hover tracking survive
        // saves.
        withMembers: members.map((member, sortOrder) => ({
          create: {
            householdID,
            name: member.name,
            relationship: member.relationship,
            description: member.description,
            sortOrder,
          },
          // Re-sent because the recreate cascade-deletes the backend's
          // generated citations along with the old members.
          withCitations: mapCitationsToCreateInput(
            member.citations,
            householdID
          ),
        })),
      };
      break;
    }
    case PresentationBundleKind.WillBundle: {
      const wills = bundle.willBundleConfiguration?.wills ?? [];
      if (!bundle.willBundleConfiguration) break;
      const willDisplayName =
        bundle.willBundleConfiguration?.displayName ?? undefined;
      output.withWillBundleConfiguration = {
        create: {
          householdID,
          displayName: willDisplayName,
          itemsPerPage: bundle.willBundleConfiguration?.itemsPerPage,
        },
        // Two-level nested children: each save recreates the bundle
        // config plus all client configurations and their items. The
        // server mints fresh ids; `syncBundlesFromResponse` keeps the
        // form's existing ids stable across persists, so React keys /
        // hover tracking survive saves. `grantorName` maps to the
        // server's `client` field.
        withClients: wills.map((will, willSortOrder) => ({
          create: {
            householdID,
            client: will.grantorName,
            sortOrder: willSortOrder,
          },
          withWillItems: will.items.map((item, itemSortOrder) => ({
            create: {
              householdID,
              label: item.label,
              body: item.body,
              sortOrder: itemSortOrder,
            },
            // Re-sent because the recreate cascade-deletes the backend's
            // generated citations along with the old items.
            withCitations: mapCitationsToCreateInput(
              item.citations,
              householdID
            ),
          })),
        })),
      };
      break;
    }
    case PresentationBundleKind.PowerOfAttorneyBundle: {
      if (!bundle.powerOfAttorneyBundleConfiguration) break;
      // Mirrors the WillBundle case — same recreate-everything strategy
      // for the two-level nested config. The schema differs only in
      // the input field names (`withPowerOfAttorneyItems` /
      // `withPowerOfAttorneyBundleConfiguration`).
      const powersOfAttorney =
        bundle.powerOfAttorneyBundleConfiguration?.powersOfAttorney ?? [];
      const poaDisplayName =
        bundle.powerOfAttorneyBundleConfiguration?.displayName ?? undefined;
      output.withPowerOfAttorneyBundleConfiguration = {
        create: {
          householdID,
          displayName: poaDisplayName,
          itemsPerPage: bundle.powerOfAttorneyBundleConfiguration?.itemsPerPage,
        },
        withClients: powersOfAttorney.map((poa, poaSortOrder) => ({
          create: {
            householdID,
            client: poa.grantorName,
            sortOrder: poaSortOrder,
          },
          withPowerOfAttorneyItems: poa.items.map((item, itemSortOrder) => ({
            create: {
              householdID,
              label: item.label,
              body: item.body,
              sortOrder: itemSortOrder,
            },
            // Re-sent because the recreate cascade-deletes the backend's
            // generated citations along with the old items.
            withCitations: mapCitationsToCreateInput(
              item.citations,
              householdID
            ),
          })),
        })),
      };
      break;
    }
    case PresentationBundleKind.HealthcareDirectiveBundle: {
      if (!bundle.healthcareDirectiveBundleConfiguration) break;
      // Mirrors the WillBundle case — same recreate-everything strategy
      // for the two-level nested config. The schema differs only in
      // the input field names (`withHealthcareDirectiveItems` /
      // `withHealthcareDirectiveBundleConfiguration`).
      const healthcareDirectives =
        bundle.healthcareDirectiveBundleConfiguration?.healthcareDirectives ??
        [];
      const hcdDisplayName =
        bundle.healthcareDirectiveBundleConfiguration?.displayName ?? undefined;
      output.withHealthcareDirectiveBundleConfiguration = {
        create: {
          householdID,
          displayName: hcdDisplayName,
          itemsPerPage:
            bundle.healthcareDirectiveBundleConfiguration?.itemsPerPage,
        },
        withClients: healthcareDirectives.map((hcd, hcdSortOrder) => ({
          create: {
            householdID,
            client: hcd.grantorName,
            sortOrder: hcdSortOrder,
          },
          withHealthcareDirectiveItems: hcd.items.map(
            (item, itemSortOrder) => ({
              create: {
                householdID,
                label: item.label,
                body: item.body,
                sortOrder: itemSortOrder,
              },
              // Re-sent because the recreate cascade-deletes the backend's
              // generated citations along with the old items.
              withCitations: mapCitationsToCreateInput(
                item.citations,
                householdID
              ),
            })
          ),
        })),
      };
      break;
    }
    case PresentationBundleKind.AssetDistributionBundle: {
      // Two-level nested children: each save recreates the bundle
      // config plus all distribution items and their detail rows. The
      // server mints fresh ids; `syncBundlesFromResponse` keeps the
      // form's existing ids stable across persists, so React keys /
      // hover tracking survive saves. `deathOrderDisplay` and
      // `description` are required strings on the schema — we always
      // send a string (empty when unset) so the create doesn't reject.
      const config = bundle.assetDistributionBundleConfiguration;
      if (!config) break;
      const items = config?.items ?? [];
      output.withAssetDistributionBundleConfiguration = {
        create: {
          householdID,
          displayName: config?.displayName ?? undefined,
          deathOrderDisplay: config?.deathOrderDisplay ?? '',
          description: config?.description ?? '',
          itemsPerPage: config?.itemsPerPage,
        },
        withDistributionItems: items.map((item, itemSortOrder) => ({
          create: {
            householdID,
            displayName: item.displayName,
            sortOrder: itemSortOrder,
          },
          withItemDetails: item.itemDetails.map((detail, detailSortOrder) => ({
            create: {
              householdID,
              label: detail.label,
              body: detail.body,
              sortOrder: detailSortOrder,
            },
            // Re-sent because the recreate cascade-deletes the backend's
            // generated citations along with the old detail rows.
            withCitations: mapCitationsToCreateInput(
              detail.citations,
              householdID
            ),
          })),
        })),
      };
      break;
    }
    case PresentationBundleKind.PlanningOpportunitiesBundle: {
      // Each save recreates the bundle config plus all items from
      // scratch — the server mints fresh ids; `syncBundlesFromResponse`
      // keeps the form's existing ids stable across persists, so React
      // keys and the toolbar's hovered-id survive saves. `sortOrder`
      // is the items-array index so reordering on the slide persists
      // verbatim. All six text fields are optional on the schema, but
      // we always send strings (empty when unset) so the slide and the
      // server agree on the canonical "empty" representation.
      const config = bundle.planningOpportunitiesBundleConfiguration;
      if (!config) break;
      const opportunities = config?.opportunities ?? [];
      output.withPlanningOpportunitiesBundleConfiguration = {
        create: {
          householdID,
          displayName: config?.displayName ?? undefined,
        },
        withItems: opportunities.map((opportunity, sortOrder) => ({
          create: {
            householdID,
            sortOrder,
            opportunityLabel: opportunity.opportunityLabel,
            opportunityBody: opportunity.opportunityBody,
            rationaleLabel: opportunity.rationaleLabel,
            rationaleBody: opportunity.rationaleBody,
            nextStepLabel: opportunity.nextStepLabel,
            nextStepBody: opportunity.nextStepBody,
          },
        })),
      };
      break;
    }
    case PresentationBundleKind.BusinessOverviewBundle: {
      const config = bundle.businessOverviewBundleConfiguration;
      if (!config) break;
      output.withBusinessOverviewBundleConfiguration = {
        create: {
          householdID,
          displayName: config.displayName ?? undefined,
          itemsPerPage: config.itemsPerPage,
        },
        // Same recreate behavior as the trusts overview: each save sends
        // every business and item, and `syncBundlesFromResponse` keeps the
        // form's ids stable.
        withBusinesses: config.businesses.map((business, businessOrder) => ({
          create: {
            householdID,
            displayName: business.label,
            sortOrder: businessOrder,
          },
          withBusinessItems: business.items.map((item, itemOrder) => ({
            create: {
              householdID,
              label: item.label,
              body: item.body,
              sortOrder: itemOrder,
            },
            // Re-sent because the recreate cascade-deletes the backend's
            // generated citations along with the old items.
            withCitations: mapCitationsToCreateInput(
              item.citations,
              householdID
            ),
          })),
        })),
      };
      break;
    }
    // no configuration object for these bundles
    case PresentationBundleKind.DiagramBundle:
    case PresentationBundleKind.EducationPageBundle:
    case PresentationBundleKind.TableOfContentsBundle:
    case PresentationBundleKind.LuminaryDisclaimerBundle:
    case PresentationBundleKind.ProfessionalTeamBundle:
    case PresentationBundleKind.StandaloneDisclaimerBundle:
    case PresentationBundleKind.DocumentSummaryBundle:
    case PresentationBundleKind.FamilyTreeBundle:
    case PresentationBundleKind.LiabilitiesBundle:
    case PresentationBundleKind.LifeInsuranceBundle: {
      break; // No special configuration needed for these bundle types
    }
    default: {
      throw new UnreachableError({
        case: kind,
        message: 'Unhandled bundle type',
      });
    }
  }

  return output;
}

export function getWaterfallWithProjections(
  input:
    | ClientPresentationDesignerV2_WaterfallBundleConfigurationFragment
    | null
    | undefined
): ClientPresentationDesignerV2_EstateWaterfallFragment | null | undefined;

export function getWaterfallWithProjections(
  input:
    | ClientPresentationDesignerV2_ComparedWaterfallFragment
    | null
    | undefined
):
  | ClientPresentationDesignerV2_ComparedWaterfall_EstateWaterfallFragment
  | null
  | undefined;

export function getWaterfallWithProjections(
  input:
    | ClientPresentationDesignerV2_WaterfallBundleConfigurationFragment
    | ClientPresentationDesignerV2_ComparedWaterfallFragment
    | null
    | undefined
):
  | ClientPresentationDesignerV2_EstateWaterfallFragment
  | ClientPresentationDesignerV2_ComparedWaterfall_EstateWaterfallFragment
  | null
  | undefined {
  const { estateWaterfall, estateWaterfallViz } = input || {};

  // estateWaterfallVizNonHypothetical is only present on the WaterfallBundleConfigurationFragment
  // and not the ComparedWaterfallFragment, so we need to check for it specifically. it's important because
  // when we're fetching waterfall data through the bundle, we need to make sure that we're respecting the
  // assumption configurations in the bundle itself. this means that we have to fetch the "WaterfallViz" type
  // via the special estateWaterfallViz and estateWaterfallVizNonHypothetical fields defined
  // directly on the bundle.
  let estateWaterfallVizNonHypothetical = null;
  if (input && 'estateWaterfallVizNonHypothetical' in input) {
    estateWaterfallVizNonHypothetical = input.estateWaterfallVizNonHypothetical;
  }

  if (!estateWaterfall) {
    return null;
  }

  const {
    firstGrantorDeathYear: estateWaterfallFirstGrantorDeathYear,
    secondGrantorDeathYear: estateWaterfallSecondGrantorDeathYear,
    growthProfile: estateWaterfallGrowthProfile,
  } = estateWaterfall;

  let outputEstateWaterfall = estateWaterfall;

  const {
    firstGrantorDeathYear: materializedFirstGrantorDeathYear,
    secondGrantorDeathYear: materializedSecondGrantorDeathYear,
    selectedGrowthProfile: materializedGrowthProfile,
  } = estateWaterfallViz?.materializedInputParams || {};

  if (estateWaterfallViz) {
    outputEstateWaterfall = {
      ...estateWaterfall,
      ...(estateWaterfallVizNonHypothetical
        ? {
            nonHypotheticalVisualizationWithProjections:
              estateWaterfallVizNonHypothetical,
          }
        : {}),
      visualizationWithProjections: estateWaterfallViz,
      firstGrantorDeathYear:
        materializedFirstGrantorDeathYear ||
        estateWaterfallFirstGrantorDeathYear,
      secondGrantorDeathYear:
        materializedSecondGrantorDeathYear ||
        estateWaterfallSecondGrantorDeathYear,
      growthProfile: materializedGrowthProfile || estateWaterfallGrowthProfile,
    };
  }

  return outputEstateWaterfall;
}
